import { useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../../component/ui/Chart";
import { useTransmissionInsights } from "../../contexts/TransmissionInsightsContext";
import _ from "lodash";
import { TIVisibleFeatures } from "../../types/types";

type GeneratorChartConfig = {
    [key: string]: {
        label: string;
        color?: string;
    };
};

const chartConfig: GeneratorChartConfig = {
    count: {
        label: "Count",
    },
    gas: {
        label: "Gas",
        color: "#006A66",
    },
    coal: {
        label: "Coal",
        color: "#2DB34A",
    },
    nuclear: {
        label: "Nuclear",
        color: "#73BF44",
    },
    other: {
        label: "Other",
        color: "#C6D92D",
    },
} as const;

export function OperatingGeneratorChart() {
    const { visibleFeatures, selectedArea } = useTransmissionInsights();

    // Determine which features to use based on whether there's a selection
    const features = useMemo(() => {
        if (selectedArea.features.length > 0) {
            return selectedArea.features.filter(
                (f) => f.type === TIVisibleFeatures.generator
            );
        }
        return visibleFeatures.filter(
            (f) => f.type === TIVisibleFeatures.generator
        );
    }, [visibleFeatures, selectedArea]);

    // Process the generator data
    const chartData = useMemo(() => {
        const generators = features.filter(
            (feature) => feature.properties?.Technology
        );

        const groupedGenerators = _.groupBy(generators, (generator) => {
            const tech = generator.properties?.Technology?.toLowerCase() || "";

            // Check for gas technologies
            if (tech.includes("gas") || tech.includes("chp")) return "gas";
            // Check for coal technologies
            if (tech.includes("coal") || tech.includes("steam")) return "coal";
            // Check for nuclear technologies
            if (tech.includes("nuclear")) return "nuclear";
            return "other";
        });

        return Object.entries(groupedGenerators).map(([type, gens]) => ({
            type,
            count: gens.length,
            fill: chartConfig[type]?.color || chartConfig.other.color,
        }));
    }, [features]);

    const totalGenerators = useMemo(() => {
        return chartData.reduce((acc, curr) => acc + curr.count, 0);
    }, [chartData]);

    if (chartData.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                No operating generators in view
            </div>
        );
    }

    return (
        <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square min-h-[180px]"
        >
            <PieChart>
                <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent hideLabel />}
                />
                <Pie
                    data={chartData}
                    dataKey="count"
                    nameKey="type"
                    innerRadius={60}
                    outerRadius={90}
                    strokeWidth={5}
                >
                    <Label
                        content={({ viewBox }) => {
                            if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                return (
                                    <text
                                        x={viewBox.cx}
                                        y={viewBox.cy}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                    >
                                        <tspan
                                            x={viewBox.cx}
                                            y={viewBox.cy}
                                            className="fill-black dark:fill-[#CEE0EF] text-3xl font-bold"
                                        >
                                            {totalGenerators.toLocaleString()}
                                        </tspan>
                                        <tspan
                                            x={viewBox.cx}
                                            y={(viewBox.cy || 0) + 24}
                                            className="fill-[#71717A] dark:fill-[#CEE0EF]"
                                        >
                                            Total
                                        </tspan>
                                    </text>
                                );
                            }
                        }}
                    />
                </Pie>
            </PieChart>
        </ChartContainer>
    );
}

export default OperatingGeneratorChart;